/** What this lesson showed about THIS student, kept as the server stated it.
 *
 * Every field here is a repetition of something the session service already told this device
 * about its own respondent: `my_response` names the answer it counted, and `correct` is the
 * verdict it gave once the teacher revealed the answer (null otherwise, and null for anything
 * the server cannot score). Nothing is derived on the device — src/live/verdict.ts explains
 * why that rule exists, and this module must not become the loophole in it.
 *
 * sessionStorage, like the join token beside it (src/live/studentSession.ts): a reload keeps
 * the lesson, closing the tab or the app ends the sitting. Never another student, ever: this
 * device only ever receives its own record.
 */
import { storageKey } from "../product";

export type Round = {
  item_id: string;
  attempt: number;
  /** The question as it was put to this student; empty when it was asked aloud. */
  stem: string;
  /** The server counted an answer from this student in this round. */
  answered: boolean;
  /** The server's verdict, exactly as it stated it; null while it has stated none. */
  correct: boolean | null;
};

const key = (sid: string) => storageKey(`lesson.${sid}`);

export function lessonRounds(sessionId: string): Round[] {
  try {
    const raw = sessionStorage.getItem(key(sessionId));
    return raw ? (JSON.parse(raw) as Round[]) : [];
  } catch {
    return [];
  }
}

/** Note a round in the order it was asked. Upsert on (item_id, attempt) — a re-vote is its own
 * round — and never unsay something the server said: a stated verdict is not replaced by the
 * silence of a later frame, and a counted answer does not become uncounted. */
export function recordRound(sessionId: string, round: Round): void {
  try {
    const rounds = lessonRounds(sessionId);
    const i = rounds.findIndex((r) => r.item_id === round.item_id && r.attempt === round.attempt);
    const merged =
      i < 0
        ? round
        : {
            ...round,
            answered: rounds[i]!.answered || round.answered,
            correct: round.correct ?? rounds[i]!.correct,
          };
    if (i < 0) rounds.push(merged);
    else rounds[i] = merged;
    sessionStorage.setItem(key(sessionId), JSON.stringify(rounds));
  } catch {
    /* a private-mode browser with no storage still runs the lesson; only the recap is lost */
  }
}

export type Tally = { asked: number; answered: number; judged: number; right: number };

/** The three honest numbers: how many rounds there were, how many this student answered, and
 * — only among the rounds the server actually judged — how many it called right. */
export function tally(rounds: Round[]): Tally {
  return {
    asked: rounds.length,
    answered: rounds.filter((r) => r.answered).length,
    judged: rounds.filter((r) => r.correct !== null).length,
    right: rounds.filter((r) => r.correct === true).length,
  };
}
